import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, aed, fmtDateTime, downloadStoredFile, openStoredFile } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { ArrowLeft, UploadSimple, FileText, DownloadSimple, PaperPlaneTilt, Receipt, WarningCircle, CheckCircle, Trash, HandPalm, Play, Eye } from "@phosphor-icons/react";
import { BoxDimensions } from "@/components/BoxDimensions";
import { ShippingInvoiceButton } from "@/components/ShippingInvoice";
import { NonDgButton } from "@/components/NonDgDeclaration";
import { DispatchInvoicesCard } from "@/components/DispatchInvoiceView";
import { DataLoggerCard } from "@/components/DataLoggerCard";

const DOCS = [
  { key: "inside_box", label: "Documents inside box" },
  { key: "outside_box", label: "Documents outside box" },
  { key: "invoice_docs_awb_outside_box", label: "Invoice, docs & AWB (outside box)" },
  { key: "awb", label: "AWB" },
];

export default function DispatchDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isDispatch } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(null);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [issuing, setIssuing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileRef = useRef();
  const pending = useRef(null);

  const load = useCallback(() => {
    api.get(`/dispatches/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Dispatch not found"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const pick = (t) => { pending.current = t; fileRef.current?.click(); };
  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    const t = pending.current;
    if (!files.length || !t) return;
    setBusy(t.key);
    let ok = 0;
    const failed = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("doc_type", t.key);
        await api.post(`/dispatches/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch (err) { failed.push(`${file.name}: ${err?.response?.data?.detail || "upload failed"}`); }
    }
    if (ok) toast.success(ok === 1 ? `${t.label} uploaded` : `${ok} files uploaded to ${t.label}`);
    failed.forEach((m) => toast.error(m));
    load();
    setBusy(null); pending.current = null; if (fileRef.current) fileRef.current.value = "";
  };

  const holdDispatch = async () => {
    const reason = window.prompt("Stop this dispatch? Enter a reason for the dispatch team (optional):");
    if (reason === null) return;
    try { await api.post(`/dispatches/${id}/hold`, { reason }); toast.success("Dispatch on hold — dispatch team alerted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to hold"); }
  };
  const releaseHold = async () => {
    try { await api.post(`/dispatches/${id}/release`); toast.success("Dispatch released — dispatch team notified"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to release"); }
  };

  const removeDoc = async (f) => {
    if (!window.confirm("Remove this file? You can upload a new one after.")) return;
    try { await api.delete(`/dispatches/${id}/documents/${f.id}`); toast.success("File removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to remove"); }
  };

  const doDispatch = async () => {
    setConfirmOpen(false);
    try {
      await api.post(`/dispatches/${id}/dispatch`);
      toast.success("Marked as dispatched — client notified");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const onDispatchClick = () => {
    if (docsIncomplete) { setConfirmOpen(true); return; }
    doDispatch();
  };

  const [existingInv, setExistingInv] = useState([]);
  const [confirmAdd, setConfirmAdd] = useState(null);
  useEffect(() => { if (isDispatch) api.get(`/dispatch-invoices/for-dispatch/${id}`).then((r) => setExistingInv(r.data)).catch(() => {}); }, [id, isDispatch]);
  const issueInvoice = async (confirmAdditional = false) => {
    if (!Number(amount)) return toast.error("Enter an amount");
    setIssuing(true);
    try {
      await api.post("/dispatch-invoices", { dispatch_id: id, amount: Number(amount), notes, confirm_additional: confirmAdditional });
      toast.success("Invoice issued to the team (AED)");
      setAmount(""); setNotes(""); setConfirmAdd(null);
      navigate("/dispatch-invoices");
    } catch (e) {
      const det = e?.response?.data?.detail;
      if (det?.code === "needs_confirmation") setConfirmAdd(det);
      else toast.error(typeof det === "string" ? det : "Failed");
    }
    finally { setIssuing(false); }
  };

  if (!d) return <div className="py-20 text-center text-muted-foreground">Loading…</div>;
  const isAirCargo = d.mode === "air_cargo";
  const chargeLabel = isAirCargo ? "Airline charge" : "Dispatch value";

  const downloadJobPdf = async () => {
    const doc = new jsPDF();
    const blue = [13, 35, 64];
    const gold = [242, 183, 5];
    doc.setFillColor(...blue); doc.rect(0, 0, 210, 34, "F");
    try {
      const dataUrl = await fetch("/heliopolis-logo.png").then((b) => b.blob()).then((b) => new Promise((res, rej) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.onerror = rej; fr.readAsDataURL(b); }));
      doc.addImage(dataUrl, "PNG", 14, 8, 18, 18);
    } catch (e) { /* logo optional */ }
    doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(15);
    doc.text("HELIOPOLIS PHARMACEUTICALS", 37, 16);
    doc.setFont("helvetica", "normal"); doc.setFontSize(9);
    doc.text("Dispatch Job", 37, 23);
    doc.setFillColor(...gold); doc.rect(0, 34, 210, 1.5, "F");
    let y = 50;
    doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
    doc.text(d.dispatch_number, 14, y);
    doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(110, 110, 110);
    doc.text(`Status: ${String(d.status).toUpperCase()}`, 196, y, { align: "right" });
    y += 12;
    const meta = [
      ["Order #", d.order_number], ["Shipment #", d.shipment_number || "—"], ["AWB", d.awb || "—"],
      ["PTN name", d.ptn_name || "—"],
      ["Method", isAirCargo ? `Air Cargo${d.airline_name ? " · " + d.airline_name : ""}` : `Courier${d.courier_name ? " · " + d.courier_name : ""}`],
    ];
    if (isAirCargo && d.destination_country) meta.push(["Destination", d.destination_country]);
    meta.forEach(([k, v]) => { doc.setTextColor(120, 120, 120); doc.text(k, 14, y); doc.setTextColor(20, 20, 20); doc.text(String(v), 60, y); y += 7; });
    if (d.data_logger?.required) {
      y += 2; doc.setFillColor(200, 30, 30); doc.rect(14, y - 5, 182, 11, "F");
      doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text("DATA LOGGER REQUIRED" + (d.data_logger.acknowledged_by ? "  —  placed by " + d.data_logger.acknowledged_by : "  —  NOT YET PLACED"), 18, y + 2);
      y += 10; doc.setFont("helvetica", "normal"); doc.setFontSize(10);
      if (d.data_logger.note) { doc.setTextColor(160, 30, 30); doc.text(String(d.data_logger.note).slice(0, 110), 14, y + 1); y += 7; }
      doc.setTextColor(20, 20, 20);
    }
    y += 6; doc.setDrawColor(...gold); doc.setLineWidth(0.8); doc.line(14, y, 196, y); y += 9;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...blue);
    doc.text("Product", 14, y); doc.text("Qty", 180, y, { align: "right" });
    doc.setDrawColor(220, 220, 220); doc.setLineWidth(0.3); doc.line(14, y + 2, 196, y + 2);
    doc.setFont("helvetica", "normal"); doc.setTextColor(20, 20, 20); y += 8;
    (d.items || []).forEach((it) => { doc.text(String(it.name).slice(0, 80), 14, y); doc.text(String(it.quantity), 180, y, { align: "right" }); y += 7; });
    doc.save(`${d.dispatch_number}.pdf`);
  };

  const itemsByOrderNum = (d.items || []).reduce((acc, it) => {
    (acc[it.order_number] = acc[it.order_number] || []).push(it);
    return acc;
  }, {});
  const docGroups = d.patients?.length
    ? d.patients.map((p) => ({
        key: p.order_id,
        label: `${p.patient_name || p.order_number} — label`,
        ptnItems: itemsByOrderNum[p.order_number] || [],
      }))
    : DOCS;

  const isConsolidated = d.patients?.length > 0;
  const missingPtns = isConsolidated
    ? d.patients.filter((p) => !(d.documents || []).some((f) => f.doc_type === p.order_id))
    : [];
  const docsIncomplete = isConsolidated && missingPtns.length > 0;
  const boxesReady = (d.boxes || []).length > 0 && d.boxes.every((b) => b.length_cm > 0 && b.width_cm > 0 && b.height_cm > 0);

  return (
    <div className="space-y-8">
      <button onClick={() => navigate("/dispatch")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-dispatch">
        <ArrowLeft size={16} /> Dispatch Jobs
      </button>
      <PageHeader
        subtitle={d.is_transit ? `Internal transit ${d.transit_number} · ${d.from_location} → ${d.to_location}${d.order_number ? ` · for order(s) ${d.order_number}` : ""}` : `Order ${d.order_number} · Shipment ${d.shipment_number || "—"}`}
        title={d.dispatch_number}
        actions={<div className="flex items-center gap-3"><Button variant="outline" size="sm" className="rounded-sm" onClick={downloadJobPdf} data-testid="dispatch-job-pdf"><DownloadSimple size={14} className="mr-1" /> Job PDF</Button><StatusBadge status={d.status} /></div>}
      />

      <div className="grid lg:grid-cols-3 gap-8 min-w-0">
        <div className="lg:col-span-2 space-y-8 min-w-0">
          <div className="hp-card p-6 space-y-4">
            {d.is_transit && <div className="rounded-sm border border-violet-200 bg-violet-50 text-violet-800 px-3 py-2 text-sm font-medium" data-testid="dispatch-transit-banner">Internal warehouse transfer {d.transit_number}: {d.from_location} → {d.to_location}. Not a client shipment — hand over to the carrier under AWB {d.awb || "—"}.{d.notes ? ` Notes: ${d.notes}` : ""}</div>}
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Dispatch details</p>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div><p className="text-xs uppercase tracking-wide text-muted-foreground">PTN name{d.patients?.length > 1 ? "s" : ""}</p>
                {d.patients?.length ? (
                  <div className="mt-0.5 space-y-0.5" data-testid="dispatch-ptn-list">
                    {d.patients.map((p, i) => (<p key={i} className="font-medium" data-testid={`dispatch-ptn-${i}`}>{p.patient_name || "—"}<span className="text-xs text-muted-foreground"> · {p.order_number}</span></p>))}
                  </div>
                ) : (<p className="font-medium mt-0.5" data-testid="dispatch-ptn">{d.ptn_name || "—"}</p>)}
              </div>
              <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Method</p><p className="font-medium mt-0.5">{isAirCargo ? `Air Cargo${d.airline_name ? ` · ${d.airline_name}` : ""}` : `Courier${d.courier_name ? ` · ${d.courier_name}` : ""}`}</p></div>
              {isAirCargo && d.destination_country && <div><p className="text-xs uppercase tracking-wide text-muted-foreground">Destination</p><p className="font-medium mt-0.5">{d.destination_country}</p></div>}
            </div>
            <div className="border border-border rounded-md overflow-hidden">
              <div className="grid grid-cols-12 px-3 py-2 bg-muted/50 text-xs uppercase tracking-wide font-semibold text-muted-foreground">
                <div className="col-span-9">Product</div><div className="col-span-3 text-right">Qty</div>
              </div>
              {(d.items || []).map((it, i) => (
                <div key={i} className="grid grid-cols-12 px-3 py-2 text-sm border-t border-border" data-testid={`dispatch-item-${i}`}>
                  <div className="col-span-9">{it.name}</div><div className="col-span-3 text-right">{it.quantity}</div>
                </div>
              ))}
            </div>
          </div>

          <DataLoggerCard dispatch={d} isDispatch={isDispatch} onChanged={load} />

          <BoxDimensions dispatch={d} onSaved={load} readOnly={d.status === "dispatched"} />

          <DispatchInvoicesCard dispatchId={id} />

          <div className="hp-card overflow-hidden">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
              <h3 className="font-heading font-bold flex items-center gap-2"><FileText size={18} className="text-primary" /> Documents</h3>
              <div className="flex items-center gap-2">
              {!isDispatch && d.status !== "dispatched" && <ShippingInvoiceButton dispatchId={id} carrier={d.shipment_carrier} onCreated={load} />}
              {!isDispatch && d.status !== "dispatched" && <NonDgButton dispatchId={id} carrier={d.shipment_carrier} onCreated={load} />}
              {isConsolidated && (
                docsIncomplete ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2.5 py-0.5 text-[11px] uppercase tracking-wide font-semibold" data-testid="docs-incomplete-badge">
                    <WarningCircle size={13} weight="fill" /> Documents incomplete · {missingPtns.length} PTN{missingPtns.length > 1 ? "s" : ""} pending
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 text-green-700 border border-green-200 px-2.5 py-0.5 text-[11px] uppercase tracking-wide font-semibold" data-testid="docs-complete-badge">
                    <CheckCircle size={13} weight="fill" /> All PTN docs uploaded
                  </span>
                )
              )}
              </div>
            </div>
            <input ref={fileRef} type="file" multiple className="hidden" onChange={upload} data-testid="dispatch-doc-input" />
            <div className="divide-y divide-border">
              {docGroups.map((t) => {
                const files = (d.documents || []).filter((f) => f.doc_type === t.key);
                return (
                  <div key={t.key} className="px-4 py-3" data-testid={`dispatch-doc-group-${t.key}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{t.label}</p>
                        {t.ptnItems?.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-0.5" data-testid={`dispatch-doc-group-items-${t.key}`}>
                            {t.ptnItems.map((it) => `${it.quantity}× ${it.name}`).join(", ")}
                          </p>
                        )}
                      </div>
                      {!isDispatch && (
                        <Button variant="outline" size="sm" className="rounded-sm" onClick={() => pick(t)} disabled={busy === t.key} data-testid={`dispatch-upload-${t.key}`}>
                          <UploadSimple size={14} className="mr-1" /> {busy === t.key ? "Uploading…" : (files.length ? "Add more" : "Upload files")}
                        </Button>
                      )}
                    </div>
                    {files.length === 0 ? <p className="text-xs text-muted-foreground mt-1">{isDispatch ? "No document available to download yet." : "Not uploaded yet."}</p> : (
                      <div className="mt-2 space-y-1.5">
                        {files.map((f) => (
                          <div key={f.id} className="flex items-center justify-between gap-3 rounded-sm bg-muted/40 px-3 py-2" data-testid={`dispatch-doc-${f.id}`}>
                            <div className="min-w-0"><p className="text-sm truncate">{f.original_filename}</p><p className="text-xs text-muted-foreground">{f.uploaded_by} · {fmtDateTime(f.uploaded_at)}</p></div>
                            <div className="flex items-center gap-1 shrink-0">
                              <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => openStoredFile(f.storage_path)} title="Open in new tab" data-testid={`dispatch-open-${f.id}`}><Eye size={16} /></Button>
                              <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => downloadStoredFile(f.storage_path, f.original_filename)} data-testid={`dispatch-download-${f.id}`}><DownloadSimple size={16} /></Button>
                              {!isDispatch && <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10" onClick={() => removeDoc(f)} data-testid={`dispatch-doc-remove-${f.id}`}><Trash size={16} /></Button>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="space-y-8">
          <div className="hp-card p-6 space-y-4">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground">Action</p>
            {d.hold?.active && (
              <div className="rounded-sm bg-red-600 text-white px-4 py-3 space-y-1" data-testid="dispatch-hold-banner">
                <p className="font-bold flex items-center gap-2 uppercase tracking-wide text-sm"><HandPalm size={18} weight="fill" /> STOP — do not dispatch</p>
                <p className="text-xs">Placed on hold by {d.hold.by} · {fmtDateTime(d.hold.at)}</p>
                {d.hold.reason && <p className="text-sm" data-testid="dispatch-hold-reason">Reason: {d.hold.reason}</p>}
                {!isDispatch && <Button size="sm" variant="secondary" className="rounded-sm mt-1" onClick={releaseHold} data-testid="dispatch-release-btn"><Play size={14} className="mr-1" /> Release dispatch</Button>}
              </div>
            )}
            {d.status === "dispatched" ? (
              <p className="text-sm text-green-700 font-medium" data-testid="dispatch-done">Dispatched by {d.dispatched_by} · {fmtDateTime(d.dispatched_at)}</p>
            ) : (
              <>
                {docsIncomplete && (
                  <div className="flex items-start gap-2 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="dispatch-docs-warning">
                    <WarningCircle size={15} weight="fill" className="shrink-0 mt-0.5" />
                    <span>{missingPtns.length} patient document{missingPtns.length > 1 ? "s are" : " is"} still missing. You can dispatch anyway, but each PTN should have its document uploaded first.</span>
                  </div>
                )}
                {!boxesReady && (
                  <div className="flex items-start gap-2 rounded-sm bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800" data-testid="dispatch-boxes-warning">
                    <WarningCircle size={15} weight="fill" className="shrink-0 mt-0.5" />
                    <span>Enter and save the dimensions of every box before marking as dispatched.</span>
                  </div>
                )}
                <Button className="w-full rounded-sm" onClick={onDispatchClick} disabled={!!d.hold?.active || !boxesReady} data-testid="dispatch-button">
                  <PaperPlaneTilt size={16} className="mr-2" /> {d.hold?.active ? "On hold — dispatch blocked" : "Mark as Dispatched"}
                </Button>
                {!isDispatch && !d.hold?.active && (
                  <Button variant="outline" className="w-full rounded-sm text-red-700 border-red-300 hover:bg-red-50" onClick={holdDispatch} data-testid="dispatch-hold-btn">
                    <HandPalm size={16} className="mr-2" /> Stop dispatch (hold)
                  </Button>
                )}
              </>
            )}
          </div>

          {isDispatch && d.hold?.active && (
            <div className="border border-red-200 bg-red-50 rounded-md p-6 text-sm text-red-800" data-testid="dispatch-invoice-blocked">
              <p className="font-semibold flex items-center gap-2"><Receipt size={16} /> Invoicing blocked</p>
              <p className="mt-1">This dispatch is on hold — invoices cannot be issued until the team releases it.</p>
            </div>
          )}
          {isDispatch && !d.hold?.active && (
            <div className="hp-card p-6 space-y-4">
              <p className="text-xs uppercase tracking-[0.2em] font-semibold text-muted-foreground flex items-center gap-2"><Receipt size={16} className="text-primary" /> Issue invoice to team (AED)</p>
              {existingInv.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="dispatch-invoice-existing">
                  <p className="font-semibold">Already invoiced for this dispatch / order:</p>
                  <ul className="mt-1 space-y-0.5">{existingInv.map((x) => <li key={x.id}>{x.dispatch_invoice_number} · {x.charge_type} · {aed(x.amount)} · {x.status}</li>)}</ul>
                  <p className="mt-1">Do not re-issue the same charge — edit the existing invoice from Dispatch Invoices instead.</p>
                </div>
              )}
              {confirmAdd && (
                <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-900 space-y-2" data-testid="dispatch-invoice-confirm-additional">
                  <p className="font-semibold">{confirmAdd.message}</p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="rounded-sm h-8" onClick={() => setConfirmAdd(null)} data-testid="dispatch-invoice-confirm-cancel">Cancel</Button>
                    <Button size="sm" className="rounded-sm h-8" onClick={() => issueInvoice(true)} disabled={issuing} data-testid="dispatch-invoice-confirm-yes">Yes, it is an additional charge</Button>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs uppercase tracking-wide">{chargeLabel} (د.إ)</Label>
                <Input type="number" min="0" step="0.01" className="mt-1.5 rounded-sm" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="dispatch-invoice-amount" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide">Notes (optional)</Label>
                <Textarea rows={2} className="mt-1.5 rounded-sm" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="dispatch-invoice-notes" />
              </div>
              <Button className="w-full rounded-sm" onClick={() => issueInvoice(false)} disabled={issuing} data-testid="dispatch-invoice-submit">
                {issuing ? "Issuing…" : `Issue ${aed(Number(amount) || 0)}`}
              </Button>
            </div>
          )}
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><WarningCircle size={20} weight="fill" className="text-amber-500" /> Documents incomplete</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">The following patient{missingPtns.length > 1 ? "s are" : " is"} missing their document. Dispatching now means these files will not be attached to the shipment.</p>
            <ul className="space-y-1.5 rounded-sm bg-muted/40 border border-border p-3">
              {missingPtns.map((p, i) => (
                <li key={i} className="text-sm flex items-center gap-2" data-testid={`missing-ptn-${i}`}>
                  <WarningCircle size={14} className="text-amber-500 shrink-0" />
                  <span className="font-medium">{p.patient_name || "—"}</span>
                  <span className="text-xs text-muted-foreground">· {p.order_number}</span>
                </li>
              ))}
            </ul>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setConfirmOpen(false)} data-testid="dispatch-cancel-confirm">Go back &amp; upload</Button>
            <Button className="rounded-sm" onClick={doDispatch} data-testid="dispatch-confirm-anyway">Dispatch anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
